import React, { useState, useEffect, useRef } from "react";

const randomHex = () => `#${Math.floor(Math.random() * 0xffffff).toString(16).padStart(6, "0")}`;
const random = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

const fonts = ["Poppins", "Roboto", "Montserrat", "Lobster", "Open Sans", "Raleway", "Pacifico", "Playfair Display", "Oswald", "Bebas Neue"];

const hslToRgb = (h, s, l) => {
  s /= 100; l /= 100;
  const k = n => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = n => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return `rgb(${Math.round(255 * f(0))}, ${Math.round(255 * f(8))}, ${Math.round(255 * f(4))})`;
};

const getContrastYIQ = hex => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 128 ? "#000" : "#fff";
};

const hsvFromHex = hex => {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const v = max;
  const s = max === 0 ? 0 : (max - min) / max;
  let h = max === min ? 0 :
    max === r ? ((g - b) / (max - min)) * 60 :
    max === g ? ((b - r) / (max - min) + 2) * 60 :
    ((r - g) / (max - min) + 4) * 60;
  if (h < 0) h += 360;
  return { h: Math.round(h), s: Math.round(s * 100), v: Math.round(v * 100) };
};

const hsvToRgb = (h, s, v) => {
  s /= 100; v /= 100;
  const c = v * s;
  const x = c * (1 - Math.abs((h / 60) % 2 - 1));
  const m = v - c;
  let r, g, b;
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  return { r: Math.round((r + m) * 255), g: Math.round((g + m) * 255), b: Math.round((b + m) * 255) };
};

const rgbToHex = (r, g, b) => `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).padStart(6, "0")}`;

// Функция для генерации похожих цветов
const generateHarmoniousPalette = (type = "analogous") => {
  // Выбираем базовый цвет
  const baseHue = random(0, 360);
  const baseSaturation = random(60, 90);
  const baseLightness = random(40, 60);
  
  const palette = [];
  
  switch(type) {
    case "monochromatic": // Монохромная палитра
      for (let i = 0; i < 6; i++) {
        // Меняем только насыщенность и яркость
        const s = Math.max(20, Math.min(100, baseSaturation + random(-15, 15)));
        const l = Math.max(20, Math.min(80, baseLightness + (i - 3) * 10));
        palette.push(hslToRgb(baseHue, s, l));
      }
      break;
      
    case "analogous": // Аналогичная палитра (соседние цвета)
      for (let i = 0; i < 6; i++) {
        const hue = (baseHue + (i - 3) * 15 + 360) % 360;
        const s = Math.max(50, Math.min(90, baseSaturation + random(-10, 10)));
        const l = Math.max(30, Math.min(70, baseLightness + random(-10, 10)));
        palette.push(hslToRgb(hue, s, l));
      }
      break;
      
    case "complementary": // Комплементарная палитра
      const complementaryHue = (baseHue + 180) % 360;
      for (let i = 0; i < 3; i++) {
        // Базовые цвета
        const hue1 = (baseHue + i * 10) % 360;
        palette.push(hslToRgb(hue1, baseSaturation + random(-10, 10), baseLightness + random(-10, 10)));
      }
      for (let i = 0; i < 3; i++) {
        // Комплементарные цвета
        const hue2 = (complementaryHue + i * 10) % 360;
        palette.push(hslToRgb(hue2, baseSaturation + random(-10, 10), baseLightness + random(-10, 10)));
      }
      break;
      
    case "triadic": // Триадная палитра
      for (let i = 0; i < 6; i++) {
        const hue = (baseHue + i * 60) % 360;
        const s = Math.max(50, Math.min(90, baseSaturation + random(-10, 10)));
        const l = Math.max(30, Math.min(70, baseLightness + random(-10, 10)));
        palette.push(hslToRgb(hue, s, l));
      }
      break;
      
    default: // По умолчанию - аналогичная
      for (let i = 0; i < 6; i++) {
        const hue = (baseHue + (i - 2) * 20 + 360) % 360;
        palette.push(hslToRgb(hue, baseSaturation, baseLightness));
      }
  }
  
  return palette;
};

const OtherTools = ({ 
  toolsList, 
  toggleTool, 
  copyToClipboard, 
  font, 
  generateFont,
  palette,
  generatePalette,
  customColor,
  setCustomColor,
  gradient,
  setGradient,
  buttonFont,
  buttonGradient,
  buttonTextColor,
  buttonBorderRadius,
  buttonBoxShadow,
  generateRandomButton
}) => {
  const [hsv, setHsv] = useState(() => hsvFromHex(customColor));
  const colorPickerRef = useRef(null);
  const hueSliderRef = useRef(null);
  const isDraggingColor = useRef(false);
  const isDraggingHue = useRef(false);
  const [paletteType, setPaletteType] = useState("analogous");

  useEffect(() => {
    const hsvValue = hsvFromHex(customColor);
    setHsv(hsvValue);
  }, [customColor]);

  useEffect(() => {
    if (!toolsList.includes("colorPicker")) return;

    const updateColor = (e, isHue = false) => {
      e.preventDefault();
      const target = isHue ? hueSliderRef.current : colorPickerRef.current;
      if (!target) return;
      const rect = target.getBoundingClientRect();
      const x = Math.max(0, Math.min((e.touches?.[0].clientX || e.clientX) - rect.left, rect.width));
      const y = Math.max(0, Math.min((e.touches?.[0].clientY || e.clientY) - rect.top, rect.height));
      let newColor;
      if (isHue) {
        const h = (x / rect.width) * 360;
        const { s, v } = hsvFromHex(customColor);
        newColor = rgbToHex(...Object.values(hsvToRgb(h, s, v)));
      } else {
        const h = (x / rect.width) * 360;
        const v = 100 - (y / rect.height) * 100;
        newColor = rgbToHex(...Object.values(hsvToRgb(h, 100, v)));
      }
      setCustomColor(newColor);
    };
    
    const colorPickerEl = colorPickerRef.current;
    const handleColorDown = (e) => {
      e.preventDefault();
      isDraggingColor.current = true;
      updateColor(e, false);
      
      const handleColorMove = (e) => {
        if (isDraggingColor.current) {
          updateColor(e, false);
        }
      };
      
      const handleColorUp = () => {
        isDraggingColor.current = false;
        document.removeEventListener("mousemove", handleColorMove);
        document.removeEventListener("mouseup", handleColorUp);
        document.removeEventListener("touchmove", handleColorMove);
        document.removeEventListener("touchend", handleColorUp);
      };
      
      document.addEventListener("mousemove", handleColorMove);
      document.addEventListener("mouseup", handleColorUp);
      document.addEventListener("touchmove", handleColorMove);
      document.addEventListener("touchend", handleColorUp);
    };

    const hueSliderEl = hueSliderRef.current;
    const handleHueDown = (e) => {
      e.preventDefault();
      isDraggingHue.current = true;
      updateColor(e, true);
      
      const handleHueMove = (e) => {
        if (isDraggingHue.current) {
          updateColor(e, true);
        }
      };
      
      const handleHueUp = () => {
        isDraggingHue.current = false;
        document.removeEventListener("mousemove", handleHueMove);
        document.removeEventListener("mouseup", handleHueUp);
        document.removeEventListener("touchmove", handleHueMove);
        document.removeEventListener("touchend", handleHueUp);
      };
      
      document.addEventListener("mousemove", handleHueMove);
      document.addEventListener("mouseup", handleHueUp);
      document.addEventListener("touchmove", handleHueMove);
      document.addEventListener("touchend", handleHueUp);
    };

    if (colorPickerEl) {
      colorPickerEl.addEventListener("mousedown", handleColorDown);
      colorPickerEl.addEventListener("touchstart", handleColorDown, { passive: false });
    }

    if (hueSliderEl) {
      hueSliderEl.addEventListener("mousedown", handleHueDown);
      hueSliderEl.addEventListener("touchstart", handleHueDown, { passive: false });
    }

    return () => {
      if (colorPickerEl) {
        colorPickerEl.removeEventListener("mousedown", handleColorDown);
        colorPickerEl.removeEventListener("touchstart", handleColorDown);
      }
      if (hueSliderEl) {
        hueSliderEl.removeEventListener("mousedown", handleHueDown);
        hueSliderEl.removeEventListener("touchstart", handleHueDown);
      }
    };
  }, [toolsList, customColor, setCustomColor]);

  const cardStyle = {
    background: "linear-gradient(145deg, #1e1e1e, #111111)",
    borderRadius: "24px",
    padding: "22px",
    boxShadow: "0 20px 50px rgba(0,0,0,0.6)",
    border: "1px solid #333",
    position: "relative",
    color: "#fff",
    width: "100%",
    maxWidth: "400px",
    boxSizing: "border-box",
    transition: "all 0.3s ease",
  };

  return (
    <div className="other-tools" style={{ display: "grid", gap: "20px", margin: "0 auto", width: "95%" }}>
      {toolsList.includes("font") && (
        <div style={cardStyle}>
          <div className="close" onClick={() => toggleTool("font")} style={{ position: "absolute", right: "15px", top: "15px", fontSize: "24px", cursor: "pointer" }}>×</div>
          <h3 style={{ textAlign: "center", margin: "0 0 20px" }}>{font}</h3>
          <p style={{ fontSize: "20px", textAlign: "center", margin: "30px 0", fontFamily: `'${font}', sans-serif` }}>
            The quick brown fox jumps over the lazy dog
          </p>
          <button onClick={generateFont} style={{ display: "block", margin: "20px auto", padding: "14px 32px", background: "#333", border: "none", borderRadius: "14px", cursor: "pointer", fontWeight: "bold" }}>
            New Font
          </button>
          <div onClick={() => copyToClipboard(`font-family: '${font}', sans-serif;`)} style={{ background: "rgba(0,0,0,0.5)", padding: "16px", borderRadius: "12px", textAlign: "center", cursor: "pointer", fontFamily: "monospace", color: "#4ade80" }}>
            {`font-family: '${font}', sans-serif;`}
          </div>
        </div>
      )}

      {toolsList.includes("colorPicker") && (
        <div style={cardStyle}>
          <div className="close" onClick={() => toggleTool("colorPicker")} style={{ position: "absolute", right: "15px", top: "15px", fontSize: "24px", cursor: "pointer" }}>×</div>
          <div ref={colorPickerRef} style={{ height: "240px", background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, transparent), ${customColor}`, borderRadius: "16px", cursor: "crosshair", position: "relative" }}>
            <div style={{ position: "absolute", width: "28px", height: "28px", border: "4px solid #fff", borderRadius: "50%", boxShadow: "0 0 16px #000", pointerEvents: "none", transform: "translate(-50%,-50%)", left: `${(hsvFromHex(customColor).h / 360) * 100}%`, top: `${(1 - hsvFromHex(customColor).v / 100) * 100}%` }} />
          </div>
          <div ref={hueSliderRef} style={{ height: "40px", margin: "20px 0", background: "linear-gradient(to right,#f00,#ff0,#0f0,#0ff,#00f,#f0f,#f00)", borderRadius: "20px", position: "relative", cursor: "pointer" }}>
            <div style={{ position: "absolute", width: "16px", height: "100%", background: "#fff", left: `${(hsvFromHex(customColor).h / 360) * 100}%`, transform: "translateX(-50%)", borderRadius: "10px", boxShadow: "0 0 12px #000" }} />
          </div>
          <div onClick={() => copyToClipboard(customColor)} style={{ background: customColor, color: getContrastYIQ(customColor), padding: "20px", borderRadius: "16px", textAlign: "center", fontWeight: "bold", fontSize: "18px", cursor: "pointer" }}>
            {customColor.toUpperCase()}
          </div>
        </div>
      )}

      {toolsList.includes("palette") && (
        <div style={cardStyle}>
          <div className="close" onClick={() => toggleTool("palette")} style={{ position: "absolute", right: "15px", top: "15px", fontSize: "24px", cursor: "pointer" }}>×</div>
          <div style={{ display: "flex", gap: "10px", marginBottom: "15px", flexWrap: "wrap", justifyContent: "center" }}>
            {["analogous", "monochromatic", "complementary", "triadic"].map(type => (
              <button
                key={type}
                onClick={() => {
                  setPaletteType(type);
                  generatePalette(type);
                }}
                style={{
                  padding: "8px 15px",
                  background: paletteType === type ? "linear-gradient(135deg, #667eea, #764ba2)" : "#333",
                  border: "none",
                  borderRadius: "8px",
                  color: "#fff",
                  fontSize: "12px",
                  cursor: "pointer",
                  textTransform: "capitalize",
                  fontWeight: paletteType === type ? "bold" : "normal"
                }}
              >
                {type === "analogous" ? "Схожие" : 
                 type === "monochromatic" ? "Моно" : 
                 type === "complementary" ? "Контраст" : "Триада"}
              </button>
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px", marginBottom: "24px" }}>
            {palette.map((c, i) => (
              <div key={i} onClick={() => copyToClipboard(c)} style={{ 
                height: "100px",  
                background: c, 
                borderRadius: "16px", 
                display: "flex", 
                flexDirection: "column",
                alignItems: "center", 
                justifyContent: "center", 
                color: getContrastYIQ(c), 
                fontWeight: "bold", 
                cursor: "pointer", 
                fontSize: "14px",
                transition: "transform 0.2s"
              }}>
                <div>{c.slice(1).toUpperCase()}</div>
                <div style={{ fontSize: "11px", opacity: 0.8, marginTop: "5px" }}>
                  Цвет {i + 1}
                </div>
              </div>
            ))}
          </div>
          <button onClick={() => generatePalette(paletteType)} style={{ 
            width: "100%", 
            padding: "16px", 
            background: "linear-gradient(135deg, #667eea, #764ba2)", 
            border: "none", 
            borderRadius: "16px", 
            cursor: "pointer", 
            fontWeight: "bold",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px"
          }}>
            <span>🎨</span>
            <span>Создать новую палитру</span>
          </button>
          <div style={{ 
            marginTop: "15px", 
            fontSize: "12px", 
            textAlign: "center", 
            color: "#888",
            padding: "10px",
            background: "rgba(255,255,255,0.05)",
            borderRadius: "8px"
          }}>
            {paletteType === "analogous" ? "Соседние цвета на цветовом круге" :
             paletteType === "monochromatic" ? "Оттенки одного цвета" :
             paletteType === "complementary" ? "Противоположные цвета + варианты" :
             "Цвета на равном расстоянии на круге"}
          </div>
        </div>
      )}

      {toolsList.includes("gradient") && (
        <div style={{ ...cardStyle, background: gradient }}>
          <div className="close" onClick={() => toggleTool("gradient")} style={{ position: "absolute", right: "15px", top: "15px", fontSize: "24px", cursor: "pointer", color: "#fff" }}>×</div>
          <div onClick={() => copyToClipboard(`background: ${gradient};`)} style={{ background: "rgba(0,0,0,0.5)", padding: "16px", borderRadius: "12px", textAlign: "center", cursor: "pointer", fontFamily: "monospace", color: "#fff" }}>
            {gradient}
          </div>
          <button onClick={() => setGradient(`linear-gradient(135deg, ${randomHex()}, ${randomHex()})`)} style={{ marginTop: "20px", position:"absolute", left:"50%", transform:"translateX(-50%)", bottom:"20px", padding: "14px 32px", background: "rgba(255,255,255,0.2)", border: "none", borderRadius: "14px", cursor: "pointer", fontWeight: "bold", color: "#fff" }}>
            Generate
          </button>
        </div>
      )}

      {toolsList.includes("buttonGenerator") && (
        <div style={cardStyle}>
          <div className="close" onClick={() => toggleTool("buttonGenerator")} style={{ position: "absolute", right: "15px", top: "15px", fontSize: "24px", cursor: "pointer" }}>×</div>
          <div style={{ textAlign: "center", margin: "20px 0 30px" }}>
            <button style={{ fontFamily: buttonFont, background: buttonGradient, color: buttonTextColor, padding: "24px 48px", border: "none", borderRadius: buttonBorderRadius + "px", boxShadow: buttonBoxShadow, fontSize: "20px", fontWeight: "bold", cursor: "pointer" }}>
              Button
            </button>
          </div>
          <button onClick={generateRandomButton} style={{ width: "100%", padding: "16px", background: "#333", border: "none", borderRadius: "16px", marginBottom: "16px", cursor: "pointer", fontWeight: "bold" }}>
            Random Button
          </button>
          <div onClick={() => copyToClipboard(`background: ${buttonGradient};\nborder-radius: ${buttonBorderRadius}px;\nbox-shadow: ${buttonBoxShadow};\ncolor: ${buttonTextColor};\nfont-family: '${buttonFont}', sans-serif;`)}
            style={{ background: "rgba(0,0,0,0.5)", padding: "16px", borderRadius: "12px", textAlign: "center", cursor: "pointer", fontFamily: "monospace", color: "#4ade80" }}>
            Copy CSS
          </div>
        </div>
      )}
    </div>
  );
};

export default OtherTools;